import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Stack } from "react-bootstrap";
import DatePicker from "./DatePicker";
import SelectFamily from "./SelectFamily";
import { fetchUserCaseMeetings, postMeeting, patchMeeting, deleteMeeting } from "../../services/httpClient";
import IconEmbeddedButton from "./IconEmbeddedButton";
import { faAdd, faArrowRight, faCheck, faClose, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import IconButton from "./IconButton";
import SelectObject from "./SelectObject";

const NextMeetingCard = ({ id, setShowToast, setToastText, setToastBody, family, primaryCM, secondaryCM }: any) => {
  const [meetings, setMeetings] = useState<any[]>([]);
  const [currentMeeting, setCurrentMeeting] = useState<number>(0);
  const [nextMeeting, setNextMeeting] = useState<any>({});
  const [meetingDate, setMeetingDate] = useState<string>("");
  const [participant, setParticipant] = useState<string>("");
  const [hostId, setHostId] = useState<string>(primaryCM?.user_id ?? secondaryCM?.user_id);
  const [isConfirmingCancel, setIsConfirmingCancel] = useState<boolean>(false);
  const [isRescheduling, setIsRescheduling] = useState<boolean>(false);
  const [isCreatingNext, setIsCreatingNext] = useState<boolean>(false);

  const hasMeeting = meetings.length > 0;
  const hasParticipant = nextMeeting?.participant_id && nextMeeting?.participant_type;
  const showMeeting = hasMeeting || isCreatingNext;
  const isReadOnly = hasMeeting && !isRescheduling && !isConfirmingCancel;

  useEffect(() => {
    getMeetings();
  }, []);

  useEffect(() => {
    if (family.length === 1 && isCreatingNext) {
      setParticipant(`${family[0].id}|${family[0].role === "family" ? "guardian" : "member"}`);
    }
  }, [isCreatingNext]);

  useEffect(() => {
    if (!hasMeeting) return;
    setMeetingDate(nextMeeting?.next_meeting_at ?? "");
    setParticipant(hasParticipant ? `${nextMeeting.participant_id}|${nextMeeting.participant_type}` : "");
    setHostId(nextMeeting?.host_id === null ? "0" : (nextMeeting?.host_id ?? primaryCM?.user_id ?? secondaryCM?.user_id));
  }, [nextMeeting]);

  const resetUIState = () => {
    setIsConfirmingCancel(false);
    setIsRescheduling(false);
    setIsCreatingNext(false);
  };

  const getMeetings = () => {
    fetchUserCaseMeetings(id).then((res: any) => {
      const now = new Date();

      const upcomingMeetings = res[0].data.filter((meeting: any) => {
        const meetingDate = new Date(meeting.next_meeting_at.replace(" ", "T"));
        return meetingDate > now;
      });

      setMeetings(upcomingMeetings);
      setCurrentMeeting(0);
    });
  };

  const familyMembers = useMemo(() => {
    const normalized = family
      .filter((f: any) => !f.deleted_at)
      .map((f: any) => ({
        id: f.id,
        type: f.role === "family" ? "guardian" : "member",
        key: `${f.firstname} ${f.lastname}`,
        name: `${f.firstname} ${f.lastname}`,
      }));

    return [...normalized];
  }, [family]);

  const activeCMs = [primaryCM, secondaryCM]
    .filter((c: any) => c != null)
    .map((c: any) => ({
      id: c.user_id,
      type: c.key,
      key: c.user_id,
      name: c.user_name,
    }));

  const saveMeeting = () => {
    const [participantId, participantType] = participant === "unassigned" ? ["0", null] : participant.split("|");

    const meeting = {
      next_meeting_at: meetingDate,
      participant_id: participantId,
      participant_type: participantType,
      host_id: hostId !== "0" ? hostId : null,
    };

    const request = isCreatingNext ? postMeeting(meeting, id) : patchMeeting(meeting, id, nextMeeting.id);

    request
      .then(() => {
        setShowToast(true);
        setToastText("Case Updated");
        setToastBody("Meeting saved");
        resetUIState();
        setCurrentMeeting(0);
        getMeetings();
      })
      .catch((err: any) => {
        console.log(err?.response?.data?.msg);
      });
  };

  const cancelMeeting = () => {
    deleteMeeting(id, nextMeeting.id)
      .then(() => {
        setShowToast(true);
        setToastText("Case Updated");
        setToastBody("Meeting canceled");
        resetUIState();
        setCurrentMeeting(0);
        getMeetings();
      })
      .catch((err: any) => {
        console.log(err?.response?.data?.msg);
      });
  };

  const clearDraft = () => {
    if (hasMeeting) {
      setMeetingDate(nextMeeting.next_meeting_at);
      setParticipant(hasParticipant ? `${nextMeeting.participant_id}|${nextMeeting.participant_type}` : "");
      setHostId(nextMeeting?.host_id === null ? "0" : nextMeeting?.host_id);
    } else {
      setMeetingDate("");
      setParticipant("");
      setHostId(primaryCM?.user_id ?? secondaryCM?.user_id);
    }
    resetUIState();
  };

  const onClickCreateNewMeeting = () => {
    setIsCreatingNext(true);
    setIsRescheduling(true);
    setMeetingDate("");
    setParticipant("");
    setHostId(primaryCM?.user_id ?? secondaryCM?.user_id);
  };

  useEffect(() => {
    if (meetings.length === 0) {
      setNextMeeting({});
    }

    if (currentMeeting >= meetings.length) {
      setCurrentMeeting(0);
      setNextMeeting(meetings[0]);
    }

    setNextMeeting(meetings[currentMeeting]);
  }, [meetings, currentMeeting]);

  const onClickNextMeeting = () => {
    if (meetings.length <= 1) return;
    setCurrentMeeting((prev: any) => (prev + 1 < meetings.length ? prev + 1 : 0));
  };

  return (
    <>
      <Card className="bg-light mb-5">
        <Card.Body>
          <Stack direction="horizontal" gap={5} className="justify-content-between">
            <h5 className="app-header-text mb-0">Upcoming Meeting</h5>
            <IconEmbeddedButton onClickButton={onClickCreateNewMeeting} tooltipText="New Meeting" icon={faAdd} />
          </Stack>
          {showMeeting && (
            <>
              <Stack gap={3} className="mt-3">
                <DatePicker
                  label="Date and Time"
                  currentValue={meetingDate}
                  setCurrentValue={setMeetingDate}
                  hasMinDate={true}
                  hasTime={true}
                  disabled={isReadOnly || isConfirmingCancel}
                />
                <SelectFamily
                  defaultSelection="Unassigned"
                  family={familyMembers}
                  familyUser={participant}
                  updateFamilyUser={(e: any) => setParticipant(e.target.value)}
                  readOnly={isReadOnly || isConfirmingCancel}
                  fullSize={true}
                />
                <SelectObject
                  initialOption="Unassigned"
                  label="Case Manager"
                  value={hostId}
                  options={activeCMs}
                  onUpdate={(e: any) => setHostId(e.target.value)}
                  disabled={isReadOnly || isConfirmingCancel}
                />
              </Stack>
              {isConfirmingCancel && (
                <div className="mt-4 text-center">
                  <p>Are you sure you want to cancel this meeting?</p>
                  <Button className="app-secondary-color button me-3" onClick={cancelMeeting}>
                    Yes
                  </Button>
                  <Button className="app-danger-color button" onClick={() => setIsConfirmingCancel(false)}>
                    No
                  </Button>
                </div>
              )}
              {!isConfirmingCancel && (
                <div className="mt-4">
                  {isReadOnly && (
                    <Stack direction="horizontal" gap={3} className="justify-content-center">
                      <IconButton onClickButton={() => setIsConfirmingCancel(true)} tooltipText="Cancel" icon={faTrash} />
                      <div className="vr" />
                      <IconButton onClickButton={() => setIsRescheduling(true)} tooltipText="Reschedule" icon={faEdit} />
                      {meetings.length > 1 && (
                        <>
                          <div className="vr" />
                          <IconButton onClickButton={onClickNextMeeting} tooltipText="Next" icon={faArrowRight} />
                        </>
                      )}
                    </Stack>
                  )}
                  {(isRescheduling || isCreatingNext) && (
                    <Stack direction="horizontal" gap={3} className="justify-content-center">
                      <IconButton onClickButton={saveMeeting} tooltipText="Save" icon={faCheck} disabled={!meetingDate} />
                      <div className="vr" />
                      <IconButton onClickButton={clearDraft} tooltipText="Cancel" icon={faClose} />
                    </Stack>
                  )}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default NextMeetingCard;
